import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Info } from 'lucide-react';

/**
 * DashboardCharts Component V4
 * 3 Interactive pie charts: Main Group, Subgroup, and Manufacturer distribution
 * - Legend on the right side with full names and part counts
 * - Maximum diameter with minimal padding
 * - Clickable segments and legend items for filtering
 * 
 * PURE PRESENTATION COMPONENT - All data passed via props
 * No internal API calls to prevent duplicate fetching
 */

// Color palette for charts
const CHART_COLORS = [
  '#6366f1', // indigo
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#f43f5e', // rose
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#14b8a6', // teal
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#a855f7', // purple
  '#d946ef', // fuchsia
];

const DashboardCharts = ({ 
  taxonomyStats,
  manufacturerStats,
  selectedMainGroup,
  onMainGroupClick,
  onSubGroupClick,
  onManufacturerClick,
  showLegend = true,
  // Pre-fetched name mappings from parent (prevents duplicate API calls)
  mainGroupNames = {},
  subGroupNames = {}
}) => {
  // Prepare Main Group distribution data with full names
  const mainGroupData = React.useMemo(() => {
    if (!taxonomyStats?.main_group_distribution) return [];
    
    return taxonomyStats.main_group_distribution
      .filter(g => g.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 8)
      .map((group, index) => ({
        name: group.code,
        fullName: mainGroupNames[group.code] || group.code,
        displayName: mainGroupNames[group.code] 
          ? `${mainGroupNames[group.code]} (${group.code})`
          : group.code,
        value: group.count,
        code: group.code,
        color: CHART_COLORS[index % CHART_COLORS.length]
      }));
  }, [taxonomyStats, mainGroupNames]);

  // Prepare Subgroup distribution data with full names
  const subGroupData = React.useMemo(() => {
    if (!taxonomyStats?.subgroup_distribution) return [];
    
    let filtered = taxonomyStats.subgroup_distribution;
    
    if (selectedMainGroup) {
      filtered = filtered.filter(s => s.main_group_code === selectedMainGroup);
    }
    
    return filtered
      .filter(s => s.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 8)
      .map((subgroup, index) => {
        const key = `${subgroup.main_group_code}:${subgroup.subgroup_code}`;
        const subgroupName = subGroupNames[key] || subgroup.subgroup_code;
        return {
          name: subgroup.subgroup_code,
          fullName: subgroupName,
          displayName: `${subgroupName} (${subgroup.main_group_code}:${subgroup.subgroup_code})`,
          value: subgroup.count,
          mainCode: subgroup.main_group_code,
          subCode: subgroup.subgroup_code,
          color: CHART_COLORS[index % CHART_COLORS.length]
        };
      });
  }, [taxonomyStats, selectedMainGroup, subGroupNames]);

  // Prepare Manufacturer distribution data
  const manufacturerData = React.useMemo(() => {
    if (!manufacturerStats || manufacturerStats.length === 0) return [];
    
    return manufacturerStats
      .filter(m => m.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 8)
      .map((mfr, index) => ({
        name: mfr.name,
        fullName: mfr.name,
        displayName: mfr.name,
        value: mfr.count,
        color: CHART_COLORS[index % CHART_COLORS.length]
      }));
  }, [manufacturerStats]);

  const totalMainGroupParts = mainGroupData.reduce((sum, g) => sum + g.value, 0);
  const totalSubGroupParts = subGroupData.reduce((sum, s) => sum + s.value, 0);
  const totalManufacturerParts = manufacturerData.reduce((sum, m) => sum + m.value, 0);

  // Get remaining count for "others"
  const getOthersCount = (data, totalFromStats) => {
    const displayedTotal = data.reduce((sum, d) => sum + d.value, 0);
    return totalFromStats - displayedTotal;
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, totalParts }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const percentage = totalParts > 0 ? ((data.value / totalParts) * 100).toFixed(1) : 0;
      return (
        <div className="bg-white px-3 py-2 shadow-lg rounded-lg border border-slate-200 z-50">
          <p className="font-semibold text-slate-800 text-sm">{data.fullName}</p>
          <p className="text-xs text-slate-600">
            <span className="font-mono font-medium">{data.value.toLocaleString()}</span> parts ({percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  // Right-side legend component - left-aligned text with compact spacing
  const RightLegend = ({ data, onClick, totalFromStats, showCode = false, type = 'main' }) => {
    const othersCount = getOthersCount(data, totalFromStats);
    const maxItems = 7;
    const displayData = data.slice(0, maxItems);
    const remainingCount = data.length - maxItems;
    
    // Format legend text based on type - code first, then truncated name
    const formatLegendText = (item) => {
      if (showCode) {
        const code = type === 'sub' ? `${item.mainCode}:${item.subCode}` : item.code;
        // Truncate name to fit, keeping code visible
        const maxNameLen = 14;
        const name = item.fullName.length > maxNameLen 
          ? item.fullName.substring(0, maxNameLen - 1) + '…' 
          : item.fullName;
        return `${name} (${code})`;
      }
      // For manufacturer - just truncate name
      const maxLen = 18;
      if (item.fullName.length > maxLen) {
        return item.fullName.substring(0, maxLen - 1) + '…';
      }
      return item.fullName;
    };
    
    return (
      <div className="flex flex-col justify-center items-start h-full pl-2 overflow-hidden">
        {displayData.map((item, index) => (
          <div
            key={index}
            className="flex items-center gap-1.5 py-[3px] cursor-pointer hover:bg-slate-50 rounded px-1.5 transition-colors group"
            onClick={() => onClick?.(item)}
            title={`${item.fullName}${showCode ? ` (${type === 'sub' ? `${item.mainCode}:${item.subCode}` : item.code})` : ''} - ${item.value} parts`}
          >
            <span 
              className="w-2 h-2 rounded-sm flex-shrink-0" 
              style={{ backgroundColor: item.color }}
            />
            <span className="text-[9px] text-slate-600 leading-tight group-hover:text-slate-800 whitespace-nowrap">
              {formatLegendText(item)}
            </span>
            <span className="text-[9px] font-mono text-slate-400 flex-shrink-0 tabular-nums group-hover:text-slate-600 ml-1">
              {item.value}
            </span>
          </div>
        ))}
        {(remainingCount > 0 || othersCount > 0) && (
          <div className="text-[8px] text-slate-400 pt-0.5 pl-1.5 italic">
            {remainingCount > 0 && `+${remainingCount} more`}
          </div>
        )}
      </div>
    );
  };

  if (!taxonomyStats) {
    return (
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white rounded-xl p-3 border border-slate-200 animate-pulse">
            <div className="h-3 bg-slate-200 rounded w-1/2 mb-2"></div>
            <div className="h-44 bg-slate-100 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  // Chart card component for consistency
  const ChartCard = ({ 
    title, 
    subtitle,
    data, 
    totalParts, 
    totalFromStats,
    onItemClick, 
    tooltipText,
    emptyText,
    showCode = false,
    legendType = 'main'
  }) => (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-2 pb-1">
        <div>
          <h3 className="font-semibold text-slate-700 text-xs">{title}</h3>
          {subtitle && <span className="text-[10px] text-slate-400">{subtitle}</span>}
        </div>
        <div className="group relative">
          <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
          <div className="absolute right-0 top-5 w-48 p-2 bg-slate-800 text-white text-[10px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
            {tooltipText}
          </div>
        </div>
      </div>
      
      {/* Chart + Legend Container */}
      {data.length > 0 ? (
        <div className="flex items-center" style={{ height: showLegend ? '170px' : '140px' }}>
          {/* Pie Chart - Centered when no legend, or left-aligned with legend */}
          <div 
            className="flex items-center justify-center" 
            style={{ width: showLegend ? '160px' : '100%', height: '100%' }}
          >
            <ResponsiveContainer width={showLegend ? 150 : 130} height={showLegend ? 150 : 130}>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={showLegend ? 40 : 35}
                  outerRadius={showLegend ? 70 : 60}
                  paddingAngle={2}
                  dataKey="value"
                  onClick={(d) => onItemClick?.(d)}
                  style={{ cursor: 'pointer' }}
                >
                  {data.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.color}
                      stroke="#fff"
                      strokeWidth={1}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip totalParts={totalParts} />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          {/* Legend - Right side (only if showLegend is true) */}
          {showLegend && (
            <div className="flex-1 min-w-0">
              <RightLegend 
                data={data} 
                onClick={onItemClick}
                totalFromStats={totalFromStats}
                showCode={showCode}
                type={legendType}
              />
            </div>
          )}
        </div>
      ) : (
        <div className={`flex items-center justify-center text-slate-400 text-xs px-3 ${showLegend ? 'h-[170px]' : 'h-[140px]'}`}>
          {emptyText}
        </div>
      )}
      
      {/* Footer */}
      <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-100">
        <p className="text-center text-[9px] text-slate-500">
          Click to filter • <span className="font-medium">{totalParts}</span> parts
        </p>
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {/* Main Group Distribution */}
      <ChartCard
        title="Main Group"
        data={mainGroupData}
        totalParts={totalMainGroupParts}
        totalFromStats={taxonomyStats.total_parts || totalMainGroupParts}
        onItemClick={(d) => onMainGroupClick?.(d.code)}
        tooltipText="Distribution of parts across main categories. Click a segment or legend item to filter."
        emptyText="No data"
        showCode={true}
        legendType="main"
      />

      {/* Subgroup Distribution */}
      <ChartCard
        title="Subgroup"
        subtitle={selectedMainGroup ? `Filtered: ${selectedMainGroup}` : null}
        data={subGroupData}
        totalParts={totalSubGroupParts}
        totalFromStats={totalSubGroupParts}
        onItemClick={(d) => onSubGroupClick?.(d.mainCode, d.subCode)}
        tooltipText={`Subgroups within ${selectedMainGroup ? `group ${selectedMainGroup}` : 'all groups'}. Click to filter.`}
        emptyText={selectedMainGroup ? 'No subgroups in this group' : 'Select a main group first'}
        showCode={true}
        legendType="sub"
      />

      {/* Manufacturer Distribution */}
      <ChartCard
        title="Manufacturer"
        data={manufacturerData}
        totalParts={totalManufacturerParts}
        totalFromStats={totalManufacturerParts}
        onItemClick={(d) => onManufacturerClick?.(d.name)}
        tooltipText="Distribution by manufacturer. Click to filter parts by manufacturer."
        emptyText="No manufacturer data available"
        showCode={false}
        legendType="mfr"
      />
    </div>
  );
};

export default DashboardCharts;
